import { ChevronDown, Crown, Layers, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  BAND_TEXT,
  candidateSubtitle,
  memberDisplayName,
  percent,
  scoreBand,
} from '@/features/entity-resolution/groupFormat'
import {
  exactGroupEligible,
  identityRequiresReview,
} from '@/features/entity-resolution/identityAssessment'
import type { GroupMember, ResolutionGroup } from '@/services/api/entity-resolution-groups'
import { cn } from '@/utils/cn'
import { IdentityAssessmentNotice } from './IdentityAssessmentNotice'
import { Avatar, AvatarStack, ScoreBar } from './parts'

interface ClusterRowProps {
  group: ResolutionGroup
  view: { canonicalId: string | undefined; selectedMemberId: string | null; expanded: boolean }
  busy: boolean
  on: {
    toggleExpand: () => void
    selectCandidate: (memberId: string) => void
    setCanonical: (memberId: string) => void
    removeCandidate: (memberId: string) => void
    mergeAll: () => void
    dismissAll: () => void
  }
}

/** One cluster: a canonical, its scored candidates, a distribution bar, and an
 *  expandable two-column grid of candidate cards. */
export function ClusterRow({ group, view, busy, on }: ClusterRowProps) {
  const canonical = group.members.find(m => m.id === view.canonicalId) ?? group.members[0]
  const canonicalName = canonical ? memberDisplayName(canonical) : 'Unknown'

  // Scores come from the backend (member → representative similarity, gated at
  // the floor); the UI never recomputes them. `null` is an unscored legacy/
  // property member — sorted last and rendered "—".
  const scored = group.members
    .filter(m => m.id !== canonical?.id)
    .map(m => ({ member: m, sim: m.similarityScore }))
    .sort((a, b) => (b.sim ?? -1) - (a.sim ?? -1))

  const counts = { exact: 0, high: 0, mid: 0 }
  for (const s of scored) counts[scoreBand(s.sim)]++
  const knownSims = scored.map(s => s.sim).filter((s): s is number => s != null)
  const min = knownSims.length ? Math.min(...knownSims) : null
  const max = knownSims.length ? Math.max(...knownSims) : null
  const isExact = exactGroupEligible(group)
  const range =
    min == null || max == null
      ? 'unscored'
      : isExact
        ? 'exact duplicates'
        : min === max
          ? `all ${percent(min)}`
          : `${percent(min)}–${percent(max)}`

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border bg-card transition-colors',
        isExact ? 'border-success/40' : 'border-border'
      )}
    >
      {/* Below lg the row stacks: canonical + chevron, then the candidates'
          summary, then the two cluster actions side by side. */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 lg:flex-nowrap">
        <button
          type="button"
          onClick={on.toggleExpand}
          className="flex min-w-0 flex-1 basis-full flex-wrap items-center gap-3 text-left lg:basis-[0%] lg:flex-nowrap"
        >
          <Avatar name={canonicalName} square />
          {/* 4.75rem = avatar + chevron + the two gaps: the name fills the first line. */}
          <div className="min-w-0 flex-1 basis-[calc(100%-4.75rem)] lg:w-56 lg:flex-none">
            <div className="whitespace-normal text-sm font-semibold text-foreground [overflow-wrap:anywhere]">
              {canonicalName}
            </div>
            <div className="flex items-center gap-1 text-3xs uppercase tracking-wider text-primary">
              <Crown className="size-3" />
              canonical
            </div>
          </div>
          <div className="order-1 lg:order-none">
            <AvatarStack
              names={scored.map(s => memberDisplayName(s.member))}
              bands={scored.map(s => scoreBand(s.sim))}
            />
          </div>
          <div className="order-1 min-w-0 flex-1 lg:order-none">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-sm font-medium text-foreground">
                {scored.length} candidates
              </span>
              {isExact ? (
                <span className="rounded-full bg-success-surface px-2 py-0.5 text-3xs font-medium text-success-foreground">
                  {range}
                </span>
              ) : (
                <span className="rounded-full bg-muted px-2 py-0.5 text-3xs font-medium text-muted-foreground">
                  {range}
                </span>
              )}
              {group.searchProperty && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-3xs font-medium text-muted-foreground"
                  title="Searched on this property and tolerance"
                >
                  <Search className="size-2.5" />
                  {group.searchProperty}
                  {group.searchThreshold != null && ` · ≥${percent(group.searchThreshold)}`}
                </span>
              )}
            </div>
            <div className="mt-1.5 max-w-72">
              <ScoreBar counts={counts} />
            </div>
          </div>
          <ChevronDown
            className={cn(
              'size-4 shrink-0 text-muted-foreground transition-transform',
              view.expanded && 'rotate-180'
            )}
          />
        </button>
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={on.dismissAll}
          title="Not duplicates — dismiss this cluster (entities stay untouched)"
          className="flex-1 text-muted-foreground lg:flex-none"
        >
          <X className="size-3.5" />
          Dismiss all
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={busy || scored.length === 0 || identityRequiresReview(group.identityAssessment)}
          onClick={on.mergeAll}
          className="flex-1 border-primary/40 text-primary hover:bg-primary/10 lg:flex-none"
        >
          <Layers className="size-3.5" />
          Merge all {scored.length}
        </Button>
      </div>

      {identityRequiresReview(group.identityAssessment) && (
        <div className="px-3 pb-3">
          <IdentityAssessmentNotice assessment={group.identityAssessment} />
        </div>
      )}

      {view.expanded && (
        <div className="grid grid-cols-1 gap-2 border-t border-border bg-background/30 p-3 md:grid-cols-2">
          {scored.map(({ member, sim }) => (
            <CandidateCard
              key={member.id}
              member={member}
              state={{ sim, selected: member.id === view.selectedMemberId, busy }}
              on={{
                select: () => on.selectCandidate(member.id),
                makeCanonical: () => on.setCanonical(member.id),
                remove: () => on.removeCandidate(member.id),
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface CandidateCardProps {
  member: GroupMember
  state: { sim: number | null; selected: boolean; busy: boolean }
  on: { select: () => void; makeCanonical: () => void; remove: () => void }
}

function CandidateCard({ member, state, on }: CandidateCardProps) {
  const band = scoreBand(state.sim)
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={on.select}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          on.select()
        }
      }}
      className={cn(
        'flex cursor-pointer items-center gap-3 rounded-lg border p-2.5 transition-colors',
        state.selected
          ? 'border-primary bg-primary/10'
          : 'border-border bg-card hover:border-primary/30'
      )}
    >
      <button
        type="button"
        onClick={e => {
          e.stopPropagation()
          on.makeCanonical()
        }}
        title="Make canonical — keep this entity"
        className="-m-2 shrink-0 p-2 text-muted-foreground/40 hover:text-primary lg:m-0 lg:p-0"
      >
        <Crown className="size-4" />
      </button>
      <div className="min-w-0 flex-1">
        <div className="whitespace-normal text-sm font-medium text-foreground [overflow-wrap:anywhere]">
          {memberDisplayName(member)}
        </div>
        <div className="whitespace-normal text-3xs text-muted-foreground [overflow-wrap:anywhere]">
          {candidateSubtitle(member)}
        </div>
      </div>
      <span className={cn('text-xs font-semibold tabular-nums', BAND_TEXT[band])}>
        {percent(state.sim)}
      </span>
      <button
        type="button"
        disabled={state.busy}
        onClick={e => {
          e.stopPropagation()
          on.remove()
        }}
        title="Not a match"
        className="-m-2 p-2 text-muted-foreground/50 hover:text-muted-foreground lg:m-0 lg:p-0"
      >
        <X className="size-3.5" />
      </button>
    </div>
  )
}
